import {
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform);

type ParsedShortcut = {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
};

export type ShortcutScope = {
  element: RefObject<HTMLElement | null>;
  modal: boolean;
};

type Binding = {
  shortcut: ParsedShortcut;
  scope: ShortcutScope | null;
  available: () => boolean;
  run: () => void;
};

const scopes: ShortcutScope[] = [];

const bindings: Binding[] = [];

export const ShortcutScopeContext = createContext<ShortcutScope | null>(null);

function parseShortcut(shortcut: string): ParsedShortcut {
  const parts = shortcut.toLowerCase().split("+");

  return {
    key: parts.find((part) => !["mod", "shift", "alt"].includes(part)) ?? "",
    mod: parts.includes("mod"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
  };
}

function matches(shortcut: ParsedShortcut, event: KeyboardEvent): boolean {
  return (
    event.key.toLowerCase() === shortcut.key &&
    (isMac ? event.metaKey : event.ctrlKey) === shortcut.mod &&
    (isMac ? !event.ctrlKey : !event.metaKey) &&
    event.shiftKey === shortcut.shift &&
    event.altKey === shortcut.alt
  );
}

function isEditable(target: EventTarget | undefined): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      target.matches("input, textarea, select, [role='textbox']"))
  );
}

function activeScope(event: KeyboardEvent): ShortcutScope | null {
  for (const node of event.composedPath()) {
    const scope = scopes.find((entry) => entry.element.current === node);

    if (scope) {
      return scope;
    }
  }

  for (let index = scopes.length - 1; index >= 0; index--) {
    const scope = scopes[index];

    if (scope.modal && scope.element.current?.isConnected) {
      return scope;
    }
  }

  return null;
}

function findBinding(event: KeyboardEvent): Binding | undefined {
  const scope = activeScope(event);

  for (let index = bindings.length - 1; index >= 0; index--) {
    const binding = bindings[index];

    if (
      binding.scope === scope &&
      matches(binding.shortcut, event) &&
      binding.available()
    ) {
      return binding;
    }
  }

  return undefined;
}

function handleKeyDown(event: KeyboardEvent) {
  const binding = findBinding(event);

  if (!binding) {
    return;
  }

  const bare = !binding.shortcut.mod && !binding.shortcut.alt;

  if (bare && isEditable(event.composedPath()[0])) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (!event.repeat) {
    binding.run();
  }
}

function register(binding: Binding) {
  if (bindings.length === 0) {
    document.addEventListener("keydown", handleKeyDown, true);
  }

  bindings.push(binding);

  return () => {
    bindings.splice(bindings.indexOf(binding), 1);

    if (bindings.length === 0) {
      document.removeEventListener("keydown", handleKeyDown, true);
    }
  };
}

export function useShortcutScope({ modal }: { modal: boolean }) {
  const element = useRef<HTMLElement | null>(null);

  const [scope] = useState<ShortcutScope>(() => ({ element, modal }));

  useLayoutEffect(() => {
    scopes.push(scope);

    return () => {
      scopes.splice(scopes.indexOf(scope), 1);
    };
  }, [scope]);

  const ref = useCallback((node: HTMLElement | null) => {
    element.current = node;
  }, []);

  return { scope, ref };
}

type UseScopedShortcutOptions = {
  enabled?: boolean;
  available?: () => boolean;
};

export function useScopedShortcut(
  shortcut: string | undefined,
  handler: () => void,
  { enabled = true, available }: UseScopedShortcutOptions = {},
) {
  const scope = useContext(ShortcutScopeContext);

  const handlerRef = useRef(handler);
  const availableRef = useRef(available);

  useEffect(() => {
    handlerRef.current = handler;
    availableRef.current = available;
  });

  useEffect(() => {
    if (!enabled || !shortcut) {
      return;
    }

    return register({
      shortcut: parseShortcut(shortcut),
      scope,
      available: () => availableRef.current?.() ?? true,
      run: () => handlerRef.current(),
    });
  }, [shortcut, scope, enabled]);
}

export function toAriaKeyShortcuts(shortcut: string): string {
  const { key, mod, shift, alt } = parseShortcut(shortcut);

  return [
    mod && (isMac ? "Meta" : "Control"),
    shift && "Shift",
    alt && "Alt",
    key.charAt(0).toUpperCase() + key.slice(1),
  ]
    .filter(Boolean)
    .join("+");
}
